import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, FormControl, FormLabel, Input, Text } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'phosphor-react';
import { toast } from 'sonner';
import { ICartorio, useCartorios } from '../../../../hooks/useCartorios';
import { IData } from '../../../../types/shared';

interface CartorioSelectProps {
  value: ICartorio | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (cartorio: ICartorio | null) => void;
}

interface CartorioOption {
  value: number;
  label: string;
  cartorio: ICartorio;
}

export const CartorioSelect: React.FC<CartorioSelectProps> = ({ value, onChange }) => {
  const queryClient = useQueryClient();
  const { listCartorios, createCartorio } = useCartorios();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Form de cadastro inline
  const [novoNome, setNovoNome] = useState('');
  const [novoTelefone, setNovoTelefone] = useState('');
  const [novoEndereco, setNovoEndereco] = useState('');
  const [novoEmail, setNovoEmail] = useState('');

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  const { data, isFetching } = useQuery<IData<ICartorio[]>>({
    queryKey: ['cartorios-select', { search: debounced, page: 1, size: 20 }],
    queryFn: listCartorios,
    enabled: menuOpen,
  });

  const options: CartorioOption[] = useMemo(
    () => (data?.data?.items ?? []).map(c => ({ value: c.id, label: c.nome, cartorio: c })),
    [data],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      createCartorio({
        nome: novoNome.trim(),
        telefone: novoTelefone.trim() || null,
        endereco: novoEndereco.trim() || null,
        email: novoEmail.trim() || null,
      }),
    onSuccess: res => {
      toast.success('Cartório cadastrado');
      queryClient.invalidateQueries({ queryKey: ['cartorios-select'] });
      onChange(res.data);
      setCreating(false);
      setNovoNome('');
      setNovoTelefone('');
      setNovoEndereco('');
      setNovoEmail('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar cartório');
    },
  });

  if (creating) {
    return (
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3} bg="gray.50">
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="sm" fontWeight="700" color="gray.700">
            Novo cartório
          </Text>
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<X size={12} />}
            onClick={() => setCreating(false)}
          >
            Cancelar
          </Button>
        </Flex>
        <Flex direction="column" gap={3}>
          <FormControl isRequired>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
              Nome
            </FormLabel>
            <Input
              size="sm"
              value={novoNome}
              onChange={e => setNovoNome(e.target.value)}
              placeholder="Nome do cartório"
              autoFocus
            />
          </FormControl>
          <Flex gap={3}>
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                Telefone
              </FormLabel>
              <Input
                size="sm"
                value={novoTelefone}
                onChange={e => setNovoTelefone(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                Email
              </FormLabel>
              <Input
                size="sm"
                type="email"
                value={novoEmail}
                onChange={e => setNovoEmail(e.target.value)}
              />
            </FormControl>
          </Flex>
          <FormControl>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
              Endereço
            </FormLabel>
            <Input size="sm" value={novoEndereco} onChange={e => setNovoEndereco(e.target.value)} />
          </FormControl>
          <Button
            size="sm"
            colorScheme="blue"
            alignSelf="flex-start"
            isDisabled={!novoNome.trim()}
            isLoading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Cadastrar e selecionar
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Flex direction="column" gap={2}>
      <Select<CartorioOption>
        placeholder="Buscar cartório pelo nome…"
        options={options}
        value={value ? { value: value.id, label: value.nome, cartorio: value } : null}
        inputValue={search}
        onInputChange={onInputChange}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        onChange={opt => onChange(opt ? opt.cartorio : null)}
        isLoading={isFetching}
        isClearable
        filterOption={null}
        noOptionsMessage={() => (debounced ? 'Nenhum cartório encontrado' : 'Digite para buscar')}
        loadingMessage={() => 'Buscando…'}
        size="sm"
      />
      <Button
        size="xs"
        variant="link"
        colorScheme="blue"
        leftIcon={<Plus size={12} weight="bold" />}
        alignSelf="flex-start"
        onClick={() => setCreating(true)}
      >
        Cadastrar novo cartório
      </Button>
    </Flex>
  );
};
